import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Bugsnag from '@bugsnag/js'
import { Link, Header, StandardCard } from '@navinc/base-react-components'
import { global, request } from '@navinc/utils'
import styled from 'styled-components'
import { usePartnerConfig } from '../hooks/use-partner-config.js'
import { getIsSessionExpired } from '../../common/utils/is-session-expired'
import { Main } from './styled'
import { CDNAsset } from './cdn-asset'
import { useEventListener } from '../hooks/use-event-listener'
import { useAccount } from '../hooks/apollo/use-account.js'
import { getDecodedToken, getToken } from '../../utils/tokens.js'

const StyledCard = styled(StandardCard)`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
`

const StyledCDNAsset = styled(CDNAsset)`
  width: 200px;
  height: 200px;
  margin-left: auto;
  margin-right: auto;
`

const fetchValidateToken = () =>
  request({
    url: '/ent-api/validate-token',
    body: { token: getToken() },
    method: 'POST',
  }).catch((error) => {
    console.warn('Trouble validating token')
    console.warn(error)
    Bugsnag.notify(error)
  })

export const WithIsAuthorized = ({ children }) => {
  const {
    partnerConfig: { loginUrl },
    getIsPartnerSeek,
  } = usePartnerConfig()
  const isPartnerSeek = getIsPartnerSeek()
  const { account: { id: accountId } = {} } = useAccount()
  const [isSameUser, setIsSameUser] = useState(true)
  const [isTokenValid, setIsTokenValid] = useState(true)
  const history = useHistory()
  const intervalRef = useRef(null)

  useEffect(() => {
    const intervalSeconds = 30
    // We need the interval id to be accessible from the whole component.
    // If we stored the id in a state variable, the component would be re-rendered
    // after the state update so a new interval will be created (this effect is triggered
    // after every re-render) leading us to the infinite loop hell.
    if (intervalRef.current === null) {
      intervalRef.current = setInterval(() => {
        // Determine if the user's token is still good
        fetchValidateToken().then((resp) => {
          if (resp?.data?.isValid === false) setIsTokenValid(false)
        })
      }, intervalSeconds * 1000)
    }

    return () => {
      clearInterval(intervalRef.current)
    }
  }, [history, isTokenValid])

  const handleVisibilityChange = () => {
    try {
      const { sub: JwtAccountId } = getDecodedToken()

      if (accountId !== JwtAccountId) {
        setIsSameUser(false)
      }
    } catch (err) {}
  }

  useEventListener('visibilitychange', handleVisibilityChange)

  if (getIsSessionExpired() || !isTokenValid) {
    // For Seek, let's just send them to the login screen
    if (isPartnerSeek && loginUrl) {
      global.location.assign(loginUrl)
      return null
    }

    return (
      <Main data-testid="invalid-token-card">
        <StyledCard>
          <Header>For your security we have logged you out. Please log in again.</Header>
          <StyledCDNAsset directory="design-assets/illustrations" filename="maintenance-error-fixing.png" />
          {/* TODO: Return with long-term solution for non-login clients. Only if the partner is able to login should they see this link */}
          {isPartnerSeek && (
            <Link href={loginUrl} target="_self">
              Log in
            </Link>
          )}
        </StyledCard>
      </Main>
    )
  }

  if (!isSameUser)
    return (
      <Main data-testid="logged-into-different-account">
        <StyledCard>
          <Header>You have logged into a different account. Please refresh the page.</Header>
          <StyledCDNAsset directory="design-assets/illustrations" filename="maintenance-error-fixing.png" />
        </StyledCard>
      </Main>
    )

  return children
}
